import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidationErrors
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { OverrideStatus } from 'src/app/enum/enum';
import { CustomToastsService } from '../../../../../../Services/custom-toasts.service';
import { WeekColorService } from '../../services/week-color.service';
import {
  ColorSchedule,
  ColorScheduleGenerate,
  ProductionCycle,
  ProductionCycleSchedule
} from '../../models/week-color.model';

declare var bootstrap: any;

@Component({
  selector: 'app-add-week-colors',
  templateUrl: './add-week-colors.component.html',
  styleUrls: ['./add-week-colors.component.css']
})
export class AddWeekColorsComponent implements OnInit {
  @Output() weekColorSaved = new EventEmitter<void>();
  @Output() weekColorDeleted = new EventEmitter<void>();
  @Output() scheduleSaved = new EventEmitter<void>();
  @Output() modalCancelled = new EventEmitter<void>();

  @ViewChild('modalConfirmCloseButton')
  modalConfirmCloseButton?: ElementRef<HTMLElement>;
  @ViewChild('addColorOffcanvas')
  addColorOffcanvasRef?: ElementRef<HTMLElement>;

  @Input() weekColorList: Array<ProductionCycle> = [];

  colorForm!: FormGroup;
  scheduleForm!: FormGroup;

  Status = OverrideStatus;

  isEditMode = false;
  editingId: number = 0;
  private editingSortOrder: number = 0;

  deleteParameter: number | number[] | null = null;
  deleteButtonClickedWithoutSelection = false;
  deleteButtonClickedWithSelection = false;
  colorName = '';

  previewScheduleList: ProductionCycleSchedule[] = [];
  productionCycleSchedule: ProductionCycleSchedule[] = [];
  isPreviewStep = false;

  private companyId = 0;

  constructor(
    private fb: FormBuilder,
    private weekColorService: WeekColorService,
    private toastr: CustomToastsService
  ) { }

  ngOnInit(): void {
    this.getCompanyId();
    this.initColorForm();
    this.initScheduleForm();
  }

  private initColorForm(): void {
    this.colorForm = this.fb.group({
      color: ['', Validators.required],
      displayName: ['', [Validators.required, Validators.minLength(1)]],
      status: [OverrideStatus.Active, Validators.required]
    });
  }

  private initScheduleForm(): void {
    this.scheduleForm = this.fb.group(
      {
        scheduleStartDate: ['', Validators.required],
        scheduleEndDate: ['', Validators.required],
        daysInCycle: [7, [Validators.required, Validators.min(1)]]
      },
      { validators: this.dateRangeValidator }
    );
  }

  private dateRangeValidator = (form: FormGroup): ValidationErrors | null => {
    const start = form.get('scheduleStartDate')?.value;
    const end = form.get('scheduleEndDate')?.value;
    const endCtrl = form.get('scheduleEndDate');

    if (!start || !end) {
      if (endCtrl?.hasError('dateRange')) {
        const errors = { ...endCtrl.errors } as any;
        delete errors.dateRange;
        const keys = Object.keys(errors || {});
        endCtrl.setErrors(keys.length ? errors : null);
      }
      return null;
    }

    const startDate = new Date(start);
    const endDate = new Date(end);

    if (endDate < startDate) {
      endCtrl?.setErrors({ ...(endCtrl.errors || {}), dateRange: true });
      return { dateRange: true };
    }

    if (endCtrl?.hasError('dateRange')) {
      const errors = { ...endCtrl.errors } as any;
      delete errors.dateRange;
      const keys = Object.keys(errors || {});
      endCtrl.setErrors(keys.length ? errors : null);
    }

    return null;
  };

  private getCompanyId(): void {
    const details = localStorage.getItem('user');
    if (details) {
      this.companyId = JSON.parse(details).companyID;
    }
  }

  private buildColorScheduleObj(): ColorScheduleGenerate {
    const formValue = this.scheduleForm.value;
    const obj = new ColorScheduleGenerate();
    obj.companyID = this.companyId;
    obj.scheduleStartDate = this.formatDate(formValue.scheduleStartDate);
    obj.scheduleEndDate = this.formatDate(formValue.scheduleEndDate);
    obj.daysInCycle = formValue.daysInCycle;
    return obj;
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  }

  get colorCtrl() {
    return this.colorForm.get('color')!;
  }
  get displayNameCtrl() {
    return this.colorForm.get('displayName')!;
  }
  get statusCtrl() {
    return this.colorForm.get('status')!;
  }

  clearForm(): void {
    this.colorForm.reset({
      color: '',
      displayName: '',
      status: OverrideStatus.Active
    });
    this.isEditMode = false;
    this.editingId = 0;
    this.closeOffcanvas('addColoroffcanvas');
  }

  changeStatus(isActive: boolean): void {
    this.statusCtrl.setValue(
      isActive ? OverrideStatus.Active : OverrideStatus.Inactive
    );
  }

  CreateProductionCycle(): void {
    if (this.colorForm.invalid) {
      this.colorForm.markAllAsTouched();
      this.toastr.error('Please fill in all required fields');
      return;
    }

    const payload: ProductionCycle = {
      ...new ProductionCycle(),
      ...this.colorForm.value,
      id: this.editingId,
      companyId: this.companyId,
      sortOrder: this.editingSortOrder
    };

    this.weekColorService.addUpdateProductionCycle(payload).subscribe({
      next: (res) => {
        if (res) {
          const msg = this.isEditMode
            ? 'Week Color has been successfully updated.'
            : 'New Week Color has been successfully added.';
          this.toastr.success(msg);
          this.clearForm();
          this.weekColorSaved.emit();
        }
      },
      error: (err) => {
        const errors = err?.error?.errors;

        if (errors && errors.length > 0) {
          errors.forEach((e: any) => {
            this.toastr.error(e.message);
          });
        } else {
          this.toastr.error('Something went wrong');
        }
      }
    });
  }

  loadProductionCycle(id: number): void {
    this.isEditMode = true;
    this.editingId = id;

    this.weekColorService.getProductionCycleById(id).subscribe({
      next: (res) => {
        const data = res as ProductionCycle;
        this.colorForm.patchValue({
          color: data.color,
          displayName: data.displayName,
          status: data.status
        });
        this.editingSortOrder = data.sortOrder;
        this.openOffcanvas('addColoroffcanvas');
      },
      error: () => this.toastr.error('Failed to load week color data')
    });
  }

  confirmDelete(id: number): void {
    this.weekColorService
      .getProductionCycles(this.companyId)
      .subscribe((res) => {
        const list = res as ProductionCycle[];
        this.colorName = list.find((c) => c.id === id)?.displayName || '';
        this.deleteButtonClickedWithoutSelection = false;
        this.deleteButtonClickedWithSelection = true;
        this.deleteParameter = id;
      });
  }

  confirmBulkDelete(ids: number[]): void {
    if (!ids?.length) {
      this.deleteButtonClickedWithoutSelection = true;
      this.deleteButtonClickedWithSelection = false;
      return;
    }
    this.deleteButtonClickedWithoutSelection = false;
    this.deleteButtonClickedWithSelection = true;
    this.deleteParameter = ids;
    this.colorName = `${ids.length} colors`;
  }

  onConfirmationCancel(): void {
    this.deleteButtonClickedWithoutSelection = false;
    this.deleteButtonClickedWithSelection = false;
    this.deleteParameter = null;
    this.colorName = '';

    const el = document.getElementById('confirmationDialog');
    try {
      if (el) {
        if (
          bootstrap &&
          bootstrap.Modal &&
          typeof bootstrap.Modal.getOrCreateInstance === 'function'
        ) {
          bootstrap.Modal.getOrCreateInstance(el).hide();
        } else {
          const jq = (window as any).jQuery || (window as any).$;
          if (jq) {
            try {
              jq(el).modal('hide');
            } catch (err) {
              this.modalConfirmCloseButton?.nativeElement.click();
            }
          } else {
            this.modalConfirmCloseButton?.nativeElement.click();
          }
        }
      }
    } catch (e) {
      try {
        this.modalConfirmCloseButton?.nativeElement.click();
      } catch (err) { }
    }
    setTimeout(() => this.modalCancelled.emit(), 50);
  }

  deleteWeekColor(deleteParameter: number | number[] | null): void {
    if (!deleteParameter) return;

    const selectedIds = Array.isArray(deleteParameter)
      ? deleteParameter.join(',')
      : deleteParameter.toString();

    this.weekColorService.deleteProductionCycle(selectedIds).subscribe({
      next: (res) => {
        if (res) {
          this.toastr.success('Colors deleted successfully.');
          this.weekColorDeleted.emit();
          this.closeModal();
        }
      },
      error: () => this.toastr.error('Failed to delete colors')
    });
  }

  markAsActive(activeParameter: number | number[]): void {
    const selectedIds = Array.isArray(activeParameter)
      ? activeParameter.join(',')
      : activeParameter.toString();

    this.weekColorService.updateProductionCycleStatus(selectedIds).subscribe({
      next: (res) => {
        if (res) {
          this.toastr.success('Colors marked as active.');
          this.weekColorSaved.emit();
        }
      },
      error: () => this.toastr.error('Failed to mark colors as active')
    });
  }

  private validateScheduleData(): boolean {
    if (this.scheduleForm.invalid) {
      this.scheduleForm.markAllAsTouched();
      this.toastr.error('Please fill in all required schedule fields');
      return false;
    }

    if (!this.weekColorList?.length) {
      this.toastr.error(
        'No active week colors available. Please add colors first.'
      );
      return false;
    }

    return true;
  }

  PreviewProductionCycleSchedule(): void {
    if (!this.validateScheduleData()) return;

    const scheduleObj = this.buildColorScheduleObj();
    this.previewScheduleList = [];

    firstValueFrom(
      this.weekColorService.previewProductionCycleSchedule(scheduleObj)
    )
      .then((result) => {
        this.previewScheduleList = result;
        this.isPreviewStep = true;
      })
      .catch((error) => {
        if (error.error && error.error.errors) {
          this.toastr.error(error.error.errors[0].message);
        } else {
          this.toastr.error('Failed to generate schedule preview');
        }
      });
  }

  AddProductionCycleSchedule(): void {
    if (!this.validateScheduleData()) return;

    const scheduleObj = this.buildColorScheduleObj();

    this.weekColorService.addProductionCycleSchedule(scheduleObj).subscribe({
      next: (result) => {
        if (result) {
          this.toastr.success('Schedule generated successfully');
        }
        this.closeOffcanvas('createscheduleoffcanvas');
        this.resetScheduleObj();
        this.scheduleSaved.emit();
      },
      error: (err) => this.toastr.error(err.message)
    });
  }

  getProductioncycleSchedule(): void {
    this.weekColorService.getProductionCycleSchedule(this.companyId).subscribe({
      next: (result) => {
        if (result)
          this.productionCycleSchedule = result as ProductionCycleSchedule[];
      },
      error: () => this.toastr.error('Failed to load schedule')
    });
  }

  resetScheduleObj(): void {
    this.scheduleForm.reset({ daysInCycle: 7 });
    this.previewScheduleList = [];
  }

  get scheduleStartDateCtrl() {
    return this.scheduleForm.get('scheduleStartDate')!;
  }
  get scheduleEndDateCtrl() {
    return this.scheduleForm.get('scheduleEndDate')!;
  }
  get daysInCycleCtrl() {
    return this.scheduleForm.get('daysInCycle')!;
  }

  closeModal(): void {
    this.modalConfirmCloseButton?.nativeElement.click();
    this.clearForm();
  }

  private openOffcanvas(offcanvasId: string): void {
    const el = document.getElementById(offcanvasId);
    const bs = (window as any).bootstrap;
    if (!el || !bs) return;
    try {
      let instance = bs.Offcanvas?.getInstance?.(el);
      if (!instance) instance = new bs.Offcanvas(el);
      instance.show();
    } catch (e) {
      el.classList.add('show');
    }
  }

  private closeOffcanvas(offcanvasId: string): void {
    const el = document.getElementById(offcanvasId);
    const bs = (window as any).bootstrap;
    if (!el || !bs) return;

    try {
      let instance = bs.Offcanvas?.getInstance?.(el);
      if (!instance) instance = new bs.Offcanvas(el);
      instance.hide();

      setTimeout(() => {
        el.classList.remove('show');
        el.setAttribute('aria-hidden', 'true');
        el.removeAttribute('style');

        const backdrop = document.querySelector('.offcanvas-backdrop');
        if (backdrop) backdrop.remove();

        document.body.classList.remove('offcanvas-open', 'modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
      }, 300);
    } catch (e) {
      el.classList.remove('show');
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('style');

      const backdrop = document.querySelector('.offcanvas-backdrop');
      if (backdrop) backdrop.remove();

      document.body.classList.remove('offcanvas-open', 'modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }
  }

  backToDatePicker(): void {
    this.isPreviewStep = false;
  }

  closeAndReset(): void {
    this.resetScheduleObj();
    this.isPreviewStep = false;
    this.closeOffcanvas('createscheduleoffcanvas');
  }
}